import React, { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Card, CardBody, CardHeader } from "reactstrap";
import Loader from "../../Components/Common/Loader";

const GuestsInHouseChart = ({ data = [], loader = false }) => {
  const [loading] = useState(false);
  const isLoading = loader || loading;

  const chartData = useMemo(() => {
    if (!data.length) {
      return {
        categories: [],
        series: [],
      };
    }

    const categories = data.map((item) => {
      const date = new Date(`${item.date}T00:00:00`);

      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      });
    });

    const series = [
      {
        name: "Guests in House",
        type: "column",
        data: data.map((item) => Number(item.guests_in_house) || 0),
      },
      {
        name: "Breakfast",
        type: "line",
        data: data.map((item) => Number(item.breakfast_percent) || 0),
      },
      {
        name: "Lunch",
        type: "line",
        data: data.map((item) => Number(item.lunch_percent) || 0),
      },
      {
        name: "Dinner",
        type: "line",
        data: data.map((item) => Number(item.dinner_percent) || 0),
      },
    ];

    return {
      categories,
      series,
    };
  }, [data]);

  const guestsMax = Math.max(
    10,
    ...chartData.series.slice(0, 1).flatMap((series) => series.data),
  );
  const guestsAxisMax = Math.ceil(guestsMax / 10) * 10 || 10;

  const captureRateMax = Math.max(
    40,
    ...chartData.series
      .slice(1)
      .flatMap((series) => series.data)
      .map((value) => Number(value) || 0),
  );
  const captureRateAxisMax = Math.ceil(captureRateMax / 10) * 10;

  const options = {
    chart: {
      type: "line",
      height: 400,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      events: {
        legendClick: () => false,
      },
    },

    colors: ["#174A96", "#0AB39C", "#F7B84B", "#F06548"],

    stroke: {
      width: [0, 2, 2, 2],
      curve: "smooth",
    },

    plotOptions: {
      bar: {
        columnWidth: "65%",
        borderRadius: 2,
      },
    },

    markers: {
      size: 0,
      hover: {
        size: 5,
      },
    },

    dataLabels: {
      enabled: false,
    },

    xaxis: {
      categories: chartData.categories,

      tooltip: {
        enabled: false,
      },

      axisBorder: {
        show: false,
      },

      axisTicks: {
        show: false,
      },

      tickAmount: Math.min(10, chartData.categories.length || 1),

      labels: {
        show: true,
        rotate: 0,
        hideOverlappingLabels: true,
        trim: false,

        style: {
          fontSize: "10px",
        },
      },
    },

    yaxis: [
      {
        seriesName: "Guests in House",
        opposite: true,
        min: 0,
        max: guestsAxisMax,
        tickAmount: 4,

        labels: {
          formatter: (value) => Math.round(value),
          style: {
            fontSize: "10px",
          },
        },
      },
      {
        seriesName: ["Breakfast", "Lunch", "Dinner"],
        min: 0,
        max: captureRateAxisMax,
        tickAmount: 4,

        labels: {
          formatter: (value) => `${Math.round(value)}%`,
          style: {
            fontSize: "10px",
          },
        },
      },
    ],

    grid: {
      borderColor: "#E5E7EB",
      strokeDashArray: 3,

      xaxis: {
        lines: {
          show: false,
        },
      },

      yaxis: {
        lines: {
          show: true,
        },
      },
    },

    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "11px",

      markers: {
        width: 8,
        height: 8,
        radius: 0,
      },

      itemMargin: {
        horizontal: 8,
      },

      onItemClick: {
        toggleDataSeries: true,
      },

      onItemHover: {
        highlightDataSeries: true,
      },
    },

    tooltip: {
      shared: true,
      intersect: false,

      onDatasetHover: {
        highlightDataSeries: true,
      },

      x: {
        formatter: (value, { dataPointIndex }) => {
          const date = new Date(`${data[dataPointIndex].date}T00:00:00`);

          return date.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          });
        },
      },

      y: {
        formatter: (value, { seriesIndex }) => {
          if (seriesIndex === 0) {
            return `${Math.round(value)} guests`;
          }

          return `${value}%`;
        },
      },
    },
  };

  return (
    <Card className="mb-3">
      <CardHeader>
        <div className="d-flex align-items-start">
          <div className="icon-box rounded bg-danger text-white">
            <i className="ri-bar-chart-fill"></i>
          </div>

          <div className="ps-2">
            <h4 className="card-title mb-1">Capture Ratio & Guests in House</h4>

            <p className="text-muted mb-0">
              Capture ratio by serving period (%) against guests in house
            </p>
          </div>
        </div>
      </CardHeader>

      <CardBody>
        {isLoading && <Loader />}

        {!isLoading && chartData.series.length > 0 && (
          <div>
            <ReactApexChart
              options={options}
              series={chartData.series}
              type="line"
              height={400}
            />
          </div>
        )}

        {!isLoading && chartData.series.length === 0 && (
          <div className="text-center text-muted py-5">No data available</div>
        )}
      </CardBody>
    </Card>
  );
};

export default GuestsInHouseChart;
